import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import PageBanner from "../../components/PageBanner";
import "../home/homeRedesign.css"; // shared primitives: .btn-ed, .eyebrow-ed, .heading-ed
import "./autonomousNew.css";
import { getAutonomousRegulations } from '../../services/data.service';
import { mediaUrl } from '../../services/assetUrl';

const EASE = [0.23, 1, 0.32, 1];
const containerV = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };
const itemV = {
  hidden: { opacity: 0, transform: "translateY(22px)" },
  show: { opacity: 1, transform: "translateY(0px)", transition: { duration: 0.45, ease: EASE } },
};

const Rise = ({ children, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, transform: "translateY(26px)" }}
    whileInView={{ opacity: 1, transform: "translateY(0px)" }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.5, ease: EASE }}
  >
    {children}
  </motion.div>
);

const sp = { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.9, strokeLinecap: "round", strokeLinejoin: "round" };
const IconDoc = (<svg {...sp}><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5M9 13h6M9 17h6" /></svg>);
const IconArrow = (<svg {...sp}><path d="M5 12h14M13 6l6 6-6 6" /></svg>);
const IconBook = (<svg {...sp}><path d="M4 5a2 2 0 0 1 2-2h7v17H6a2 2 0 0 0-2 2zM20 5a2 2 0 0 0-2-2h-5v17h5a2 2 0 0 1 2 2z" /></svg>);
const IconCheck = (<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm-1 14.4-4-4 1.4-1.42L11 13.6l5.6-5.6L18 9.4Z" /></svg>);
const IconChevron = (<svg {...sp}><path d="m9 6 6 6-6 6" /></svg>);

const META_ICONS = {
  calendar: (<svg {...sp}><rect x="3" y="4.5" width="18" height="17" rx="2.5" /><path d="M16 2.5v4M8 2.5v4M3 10h18" /></svg>),
  shield: (<svg {...sp}><path d="M12 3 5 6v5c0 4.4 3 8 7 10 4-2 7-5.6 7-10V6z" /><path d="m9 12 2 2 4-4" /></svg>),
  doc: IconDoc,
};
const HL_ICONS = {
  guidelines: (<svg {...sp}><path d="M4 5a2 2 0 0 1 2-2h12v18H6a2 2 0 0 1-2-2z" /><path d="M8 7h7M8 11h7M8 15h4" /></svg>),
  pattern: (<svg {...sp}><path d="M8 6h13M8 12h13M8 18h13" /><path d="M3 6h.01M3 12h.01M3 18h.01" /></svg>),
  batch: (<svg {...sp}><path d="m12 3 9 5-9 5-9-5 9-5Z" /><path d="m3 12 9 5 9-5" /><path d="m3 17 9 5 9-5" /></svg>),
  degree: (<svg {...sp}><path d="M22 10 12 5 2 10l10 5 10-5z" /><path d="M6 12v5c0 1 2.7 2 6 2s6-1 6-2v-5" /></svg>),
};
const CONTACT_ICONS = {
  mail: (<svg {...sp}><rect x="3" y="5" width="18" height="14" rx="2.5" /><path d="m3 7 9 6 9-6" /></svg>),
  phone: (<svg {...sp}><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8.1 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z" /></svg>),
  pin: (<svg {...sp}><path d="M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11Z" /><circle cx="12" cy="10" r="2.5" /></svg>),
};
const IconHeadset = (<svg {...sp}><path d="M4 14v-2a8 8 0 0 1 16 0v2" /><path d="M4 14a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-2a2 2 0 0 1 2-2ZM20 14a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2 2 2 0 0 0 2-2v-2a2 2 0 0 0-2-2Z" /><path d="M18 20a4 4 0 0 1-4 3h-2" /></svg>);

const AutonomousRegulations = () => {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Load content from the backend (singleton).
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getAutonomousRegulations();
        if (active) setData(res?.data || null);
      } catch (e) {
        if (active) setData(null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  if (loading || !data) {
    return (
      <div className="arx-page">
        <Header parentMenu="outreach" topbarEnable="enable" />
        <PageBanner title="Autonomous Regulations" />
        <div className="container" style={{ padding: "6rem 0", textAlign: "center", color: "#53545b" }}>
          {loading ? "Loading…" : "Autonomous regulations are currently unavailable. Please check back later."}
        </div>
        <Footer />
      </div>
    );
  }

  const { hero, primary, supplementary, cta } = data;

  return (
    <div className="arx-page">
      <Header parentMenu="outreach" topbarEnable="enable" />

      <PageBanner
        title={hero.title}
        eyebrow={hero.eyebrow}
        subtitle={hero.subtitle}
        image={hero.bannerImage ? mediaUrl(hero.bannerImage) : undefined}
      />

      {/* ---- Primary document ---- */}
      <section className="arx-primary-sec">
        <div className="container">
          <Rise>
            <div className="arx-primary">
              <div className="arx-primary__left">
                <span className="arx-primary__eyebrow"><span className="arx-primary__eyebrow-ic">{IconDoc}</span>{primary.eyebrow}</span>
                <h2 className="arx-primary__title">{primary.heading}</h2>
                <p className="arx-primary__text">{primary.text}</p>
                <div className="arx-primary__meta">
                  {primary.meta.map((m, i) => (
                    <div className="arx-meta" key={i}>
                      <span className="arx-meta__icon">{META_ICONS[m.icon]}</span>
                      <span className="arx-meta__body">
                        <span className="arx-meta__label">{m.label}</span>
                        <span className="arx-meta__value">{m.value}</span>
                      </span>
                    </div>
                  ))}
                </div>
                <a className="arx-primary__btn" href={mediaUrl(primary.pdf)} target="_blank" rel="noopener noreferrer">
                  <span className="arx-primary__btn-ic">{IconBook}</span>
                  {primary.cta}
                  <span className="arx-primary__btn-arrow">{IconArrow}</span>
                </a>
              </div>

              <div className="arx-primary__right">
                <span className="arx-blob arx-blob--navy" aria-hidden="true" />
                <span className="arx-blob arx-blob--orange" aria-hidden="true" />
                <span className="arx-ring" aria-hidden="true" />
                <div className="arx-primary__doc">
                  <img src={mediaUrl(primary.image)} alt={primary.heading} loading="lazy" />
                </div>
                <div className="arx-primary__chips">
                  {primary.highlights.map((h, i) => (
                    <div className="arx-chip" key={i}>
                      <span className="arx-chip__ic">{HL_ICONS[h.icon]}</span>
                      <span>{h.text}</span>
                    </div>
                  ))}
                </div>
                <span className="arx-dots" aria-hidden="true" />
              </div>
            </div>
          </Rise>
        </div>
      </section>

      {/* ---- Related documents ---- */}
      <section className="arx-docs-sec">
        <div className="container">
          <Rise>
            <div className="arx-head">
              <span className="eyebrow-ed">{supplementary.eyebrow}</span>
              <h2 className="arx-docs__title">{supplementary.heading}</h2>
              <p className="arx-docs__intro">{supplementary.intro}</p>
            </div>
          </Rise>
          <motion.div className="arx-docs" variants={containerV} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}>
            {supplementary.docs.map((doc, i) => (
              <motion.a key={i} className={`arx-doc arx-doc--${doc.accent}`} href={mediaUrl(doc.pdf)} target="_blank" rel="noopener noreferrer" variants={itemV}>
                <div className="arx-doc__top">
                  <span className="arx-doc__icon">{IconDoc}</span>
                  <span className="arx-doc__tag">{doc.tag}</span>
                </div>
                <h3 className="arx-doc__title">{doc.title}</h3>
                <p className="arx-doc__desc">{doc.desc}</p>
                <div className="arx-doc__foot">
                  <span className="arx-doc__foot-label"><span className="arx-doc__check">{IconCheck}</span>{doc.type}</span>
                  <span className="arx-doc__foot-arrow">{IconArrow}</span>
                </div>
              </motion.a>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ---- Need assistance ---- */}
      <section className="arx-help-sec">
        <div className="container">
          <Rise>
            <div className="arx-help">
              <div className="arx-help__left">
                <span className="arx-help__illus" aria-hidden="true">{IconHeadset}</span>
                <span className="arx-help__eyebrow">{cta.eyebrow}</span>
                <h2 className="arx-help__title">{cta.heading} <span>{cta.headingHighlight}</span></h2>
                <p className="arx-help__text">{cta.text}</p>
                <div className="arx-help__buttons">
                  <Link to={cta.primary.link} className="arx-help__btn arx-help__btn--primary">
                    <span className="arx-help__btn-ic">{IconHeadset}</span>{cta.primary.text}
                  </Link>
                  <Link to={cta.secondary.link} className="arx-help__btn arx-help__btn--ghost">
                    <span className="arx-help__btn-ic">{IconDoc}</span>{cta.secondary.text}
                  </Link>
                </div>
              </div>
              <div className="arx-help__right">
                <h3 className="arx-help__support">{cta.supportHeading}</h3>
                <div className="arx-help__contacts">
                  {cta.contacts.map((c, i) => {
                    const Tag = c.href ? "a" : "div";
                    const ext = c.href && /^https?:/.test(c.href);
                    return (
                      <Tag key={i} className="arx-contact" {...(c.href ? { href: c.href } : {})} {...(ext ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
                        <span className="arx-contact__icon">{CONTACT_ICONS[c.icon]}</span>
                        <span className="arx-contact__body">
                          <span className="arx-contact__label">{c.label}</span>
                          <span className="arx-contact__value">{c.value}</span>
                        </span>
                        <span className="arx-contact__chev">{IconChevron}</span>
                      </Tag>
                    );
                  })}
                </div>
              </div>
            </div>
          </Rise>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AutonomousRegulations;
